import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_PAGE_VISIBILITY,
  applyVisibilityOverrides,
  type PageVisibilityKey,
  type PageVisibilityMap,
} from '../lib/pageVisibility';

interface PageVisibilityState {
  map: PageVisibilityMap;
  loading: boolean;
}

let cachedState: PageVisibilityState | null = null;
let inFlight: Promise<PageVisibilityState> | null = null;

const loadVisibility = async (): Promise<PageVisibilityState> => {
  if (cachedState) return cachedState;
  if (inFlight) return inFlight;

  inFlight = fetch('/api/page-visibility')
    .then(async response => {
      if (!response.ok) {
        throw new Error('Failed to load page visibility');
      }
      return response.json();
    })
    .then(payload => {
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const map = applyVisibilityOverrides(items);
      cachedState = { map, loading: false };
      return cachedState;
    })
    .catch(() => {
      cachedState = { map: DEFAULT_PAGE_VISIBILITY, loading: false };
      return cachedState;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

export const usePageVisibility = () => {
  const [state, setState] = useState<PageVisibilityState>({
    map: DEFAULT_PAGE_VISIBILITY,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    loadVisibility().then(result => {
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, []);

  const isVisible = useMemo(() => {
    return (key: PageVisibilityKey) => state.map[key] ?? true;
  }, [state.map]);

  return {
    visibility: state.map,
    loading: state.loading,
    isVisible,
    refresh: async () => {
      cachedState = null;
      const result = await loadVisibility();
      setState(result);
    },
  };
};
